import { TooltipContent } from '@design-system';
import { getLtcPool } from '@shared/calculations/funding/projection/projectionTotals';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import type { StrategyPresentation } from './types';

// Traditional / standalone LTC (generic + CareScout Care Assurance). A single
// coverage pool drawn by reimbursement (or cash indemnity), no second pool and
// no death benefit. The two per-cell tooltips below explain the "Projected
// payout" and "Pool remaining" columns, which otherwise had no hover for this
// product family. All values are read off the precomputed month state/deltas —
// the presentation never re-derives strategy math.

export const traditionalLtcPresentation: StrategyPresentation = {
  // "Pool remaining" cell — single-pool open → close breakdown. Traditional
  // LTC has one coverage pool (no AOB/COB split, no death benefit), and its
  // inflation rider grows the *remaining* balance at each policy anniversary.
  poolCellTooltip: ({ month, openState, terms }) => {
    // Unlimited pools have no meaningful running balance (the sentinel drifts
    // down by draws), so the open→close breakdown would show a misleading ~$1B.
    // Explain the unlimited coverage instead.
    if (terms.isUnlimited) {
      return (
        <TooltipContent className="max-w-xs text-xs">
          <div className="mb-1 font-semibold">LTC coverage pool — Unlimited</div>
          <div className="text-[10px] text-gray-300">
            This policy has no lifetime maximum, so the coverage pool never depletes. Each month
            still pays up to the monthly maximum; any care cost above that cap is out of pocket.
          </div>
        </TooltipContent>
      );
    }

    const opening = getLtcPool(openState);
    const closing = getLtcPool(month.state);
    const accrual = month.deltas.base.inflationAccrual;
    const drawn = month.deltas.base.depletion;

    if (opening === 0 && closing === 0 && accrual === 0 && drawn === 0) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC coverage pool"
        description="Traditional LTC reimburses care from a single coverage pool. Unused daily benefit stays in the pool; with an inflation rider, the remaining balance grows at each policy anniversary."
        sections={[
          {
            opening,
            closing,
            lines: [
              { label: 'Inflation accrual', value: accrual, kind: 'add' },
              { label: 'Drawn this month', value: drawn, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  // "Projected payout" cell — reimbursement (or indemnity) waterfall from care
  // cost to policy paid to out-of-pocket. Single-pool draw, so there is no
  // AOB/COB split line.
  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    // Nothing meaningful to break down on a fully-ineligible month (waiting
    // period / <2 ADL) — PaidCell shows the zero-row reason natively.
    if (eligibleDays === 0) return null;

    const cost = month.facts.effectiveCareCost;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawn = month.deltas.base.depletion;
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;

    const isIndemnity = terms.isIndemnity;
    // Eligible cost the policy didn't cover. With pool still open it was the
    // monthly cap that bound; with the pool closed at $0 it was exhaustion.
    const poolExhausted = getLtcPool(month.state) === 0;
    const eligibleUnpaid = Math.max(eligibleCost - paid, 0);
    const totalOop = Math.max(cost - paid, 0);
    // Per-bucket OOP from ineligible days, using the calc-supplied day
    // breakdown so a WP-only row never claims "<2 ADL" contributed (and a
    // transition month splits its cost into the two buckets honestly).
    const breakdown = month.events.ineligibleDayBreakdown;
    const oopLt2Adl = breakdown.lt2AdlDays * dailyCost;
    const oopWp = breakdown.inWpDays * dailyCost;
    const oopIl = breakdown.ilNotCoveredDays * dailyCost;
    const oopPrePolicy = breakdown.prePolicyDays * dailyCost;
    // Indemnity can pay more than the incurred cost — surplus cash, not OOP.
    const cashSurplus = Math.max(paid - cost, 0);

    const mode = isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = isIndemnity
      ? 'Pays a fixed cash benefit up to the monthly cap regardless of actual cost, drawn from the LTC pool.'
      : 'Reimburses eligible incurred cost, up to the monthly cap, drawn from the LTC pool.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Care cost</span>
          <span className="font-semibold tabular-nums">{currency(cost)}</span>
          {isPartial && (
            <>
              <span className="text-gray-300">
                Eligible ({eligibleDays}/{DAYS_IN_MONTH} days)
              </span>
              <span className="font-semibold tabular-nums">{currency(eligibleCost)}</span>
            </>
          )}
          <span className="text-gray-300">Policy paid</span>
          <span className="font-semibold tabular-nums">−{currency(paid)}</span>
          {cashSurplus > 0 ? (
            <>
              <span className="text-gray-300">Cash above cost</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(cashSurplus)}
              </span>
            </>
          ) : (
            <>
              <span className="text-gray-300">Out of pocket</span>
              <span className="font-semibold tabular-nums text-rose-300">
                {currency(totalOop)}
              </span>
              {eligibleUnpaid > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    {poolExhausted ? '· pool exhausted' : '· over monthly cap'}
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(eligibleUnpaid)}</span>
                </>
              )}
              {oopWp > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · waiting period <span className="text-gray-500">· {breakdown.inWpDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopWp)}</span>
                </>
              )}
              {oopLt2Adl > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    {'· <2 ADL'} <span className="text-gray-500">· {breakdown.lt2AdlDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopLt2Adl)}</span>
                </>
              )}
              {oopIl > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · independent living
                    <span className="text-gray-500"> · {breakdown.ilNotCoveredDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopIl)}</span>
                </>
              )}
              {oopPrePolicy > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · before policy start
                    <span className="text-gray-500"> · {breakdown.prePolicyDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopPrePolicy)}</span>
                </>
              )}
            </>
          )}
        </div>
        <div className="mt-1 text-[10px] text-gray-400">Drawn from LTC pool {currency(drawn)}</div>
      </TooltipContent>
    );
  },

  // Traditional / standalone LTC has no death benefit, and its single coverage
  // pool inflates the *remaining* balance each anniversary — a "theoretical
  // inflated ceiling assuming no draws" doesn't model anything meaningful for
  // this product once any dollar has been drawn, so we hide that column too.
  hiddenColumns: ['pool-limit', 'death-benefit'],

  // Single coverage pool — drop the AOB/COB framing in the Drawn cell.
  drawnCellLabels: {
    description:
      'Cumulative dollars drawn from the LTC coverage pool. ' +
      'Drawn = Σ (monthly pool depletion). ' +
      'Traditional LTC inflates the remaining balance each anniversary, so the pool itself can keep growing — this column counts only what has been paid out.',
    baseLabel: 'Coverage pool drawn',
    singlePool: true,
  },

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC coverage pool balance, with this month's change beneath. Traditional LTC reimburses from a single coverage pool; with an inflation rider the remaining balance grows at each policy anniversary."
        formulas={[
          { label: 'Closing pool', expression: 'opening + inflation accrual − drawn this month' },
          { label: 'Inflation accrual', expression: 'opening × inflation rate (at each policy anniversary)' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month, with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          { label: 'Reimbursement mode', expression: 'min(eligible cost, monthly maximum, pool remaining)' },
          { label: 'Indemnity mode', expression: 'min(monthly maximum, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period days and days the insured fails the 2-ADL trigger."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost × <GrowthFactor rate="care-cost inflation" exponent="years from today" />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this policy year, with the daily equivalent beneath."
        formulas={[
          {
            label: 'Monthly max',
            expression: (
              <>
                at-issue daily × days in month × <GrowthFactor rate="rider inflation rate" exponent="policy year − 1" />
              </>
            ),
          },
        ]}
        notes="Capped at the rider's inflation cap year if specified. Simple-interest riders use (1 + rate × years) instead of compounding."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative dollars drawn from the LTC coverage pool through this month."
        formulas={[
          { label: 'Drawn', expression: 'Σ (monthly pool depletion)' },
        ]}
        notes="Traditional LTC inflates the remaining balance each anniversary, so the pool itself can keep growing — this column counts only what has been paid out, not a ceiling."
      />
    ),
  },
};
